import { digitsOnlyMoney, parseMoneyForApi } from 'utils/moneyInput'

import { formatCanadianPostalCodeInput } from './createAdAddressValidation'
import {
  hydrateListingPhotos,
  listingPhotosToFlatUrls,
  ListingPhoto,
  serializeListingPhotos,
} from './listingPhotos'

export const LISTING_TYPE_KEYS = ['lease_transfer', 'sublease', 'for_rent'] as const
export type ListingType = (typeof LISTING_TYPE_KEYS)[number]

export const FLOOR_OPTION_KEYS = [
  'sous_sol',
  'demi_sous_sol',
  'rez_de_chaussee',
  '1',
  '2',
  '3',
  '4',
  '5_plus',
  'dernier_etage',
  'non_applicable',
] as const
export type FloorOption = (typeof FLOOR_OPTION_KEYS)[number]

export function serializeFloorForApi(floor: string, floorCustom: string): string {
  if (!floor) return ''
  if (floor === '5_plus' && floorCustom.trim()) {
    return `${floor}:${floorCustom.trim()}`
  }
  return floor
}

export function parseFloorFromApi(raw: unknown): { floor: string; floorCustom: string } {
  const value = raw === undefined || raw === null ? '' : String(raw).trim()
  if (!value) return { floor: '', floorCustom: '' }

  const plusMatch = value.match(/^5_plus:(\d+)$/)
  if (plusMatch) {
    return { floor: '5_plus', floorCustom: plusMatch[1] }
  }

  if ((FLOOR_OPTION_KEYS as readonly string[]).includes(value)) {
    return { floor: value, floorCustom: '' }
  }

  return { floor: '', floorCustom: '' }
}

export type RoomCount = 'studio' | '2.5' | '3.5' | '4.5' | '5.5+'

export type LaundryOption = 'in_unit' | 'in_building' | 'none'

export type PetsOption = 'yes' | 'no' | 'conditions'

/** Maps legacy `conditions` API value to form state (`yes` + optional note). */
export function normalizePetsForForm(code: string): PetsOption | '' {
  if (code === 'conditions' || code === 'yes') return 'yes'
  if (code === 'no') return 'no'
  return ''
}

export type ParkingType = 'none' | 'outdoor' | 'indoor' | 'garage'

export type ElectromenagersMode = 'included' | 'paid' | 'no' | ''

export function petsAllowsConditionsNote(pets: PetsOption | ''): boolean {
  return pets === 'yes' || pets === 'conditions'
}

export function normalizeElectromenagersFromApi(meta: {
  electromenagersMode?: unknown
  servicesIncluded?: unknown
}): ElectromenagersMode {
  const mode = toStringValue(meta.electromenagersMode)
  if (mode === 'included' || mode === 'paid' || mode === 'no') return mode
  const included = Array.isArray(meta.servicesIncluded) ? meta.servicesIncluded : []
  if (included.includes('electromenagers')) return 'included'
  return ''
}

export const TRANSFER_REASON_KEYS = [
  'jobRelocation',
  'family',
  'purchase',
  'financial',
  'lifestyle',
  'other',
] as const

export type TransferReason = (typeof TRANSFER_REASON_KEYS)[number]

export interface CreateAdFlowForm {
  listingType: ListingType | ''
  unitType: string
  street: string
  unit: string
  city: string
  postalCode: string
  neighborhood: string
  province: string
  country: string
  latitude: number | null
  longitude: number | null
  monthlyRent: string
  roomCount: RoomCount | ''
  bathrooms: string
  floor: string
  floorCustom: string
  areaSqFt: string
  services: Record<string, boolean>
  laundry: LaundryOption | ''
  pets: PetsOption | ''
  petsConditionsNote: string
  smokerAllowed: boolean
  pmrAccessible: boolean
  shortTermOk: boolean
  parkingType: ParkingType | ''
  parkingSpaces: string
  parkingPrice: string
  parkingExtraNote: string
  parkingEv: boolean
  parkingBike: boolean
  parkingLocker: boolean
  electromenagersMode: ElectromenagersMode
  electromenagersPaidNote: string
  amenities: Record<string, boolean>
  /** Uploaded photos grouped by room/category. */
  listingPhotos: ListingPhoto[]
  /** Short listing headline (max 80 characters). */
  listingTitle: string
  description: string
  /** Optional extra details (max 300 characters), stored in listing_meta. */
  supplementaryInfo: string
  guestEmail: string
  /** Full date YYYY-MM-DD — desired move-in for the assignee. */
  desiredMoveInDate: string
  /** Full date YYYY-MM-DD — lease end. */
  contractEndDate: string
  /** Full date YYYY-MM-DD — unit available from. */
  availabilityDate: string
  /** Optional reason for lease transfer. */
  transferReason: TransferReason | ''
  /** Optional cash incentive offered to assignee (digits only in UI). */
  transferIncentive: string
  virtualTourLink: string
  photosRightsConfirmed: boolean
}

export const SERVICE_KEYS = [
  'electricite',
  'eauChaude',
  'chauffage',
  'internet',
  'climatisation',
  'electromenagers',
  'meuble',
] as const

export const AMENITY_KEYS = [
  'ascenseur',
  'balconTerrasse',
  'piscine',
  'salleExercice',
  'salleCommunautaire',
  'salleJeux',
  'conciergerie',
  'isolationInsonorisation',
  'beton',
  'chuteDechets',
  'sauna',
  'jardinCommun',
  'interphone',
  'rangementExterieur',
  'accesMetro',
] as const

export const initialCreateAdFlowForm = (): CreateAdFlowForm => ({
  listingType: 'lease_transfer',
  unitType: '',
  street: '',
  unit: '',
  city: '',
  postalCode: '',
  neighborhood: '',
  province: 'QC',
  country: 'Canada',
  latitude: null,
  longitude: null,
  monthlyRent: '',
  roomCount: '',
  bathrooms: '1',
  floor: '',
  floorCustom: '',
  areaSqFt: '',
  services: Object.fromEntries(SERVICE_KEYS.map(k => [k, false])) as Record<
    string,
    boolean
  >,
  laundry: '',
  pets: '',
  petsConditionsNote: '',
  smokerAllowed: false,
  pmrAccessible: false,
  shortTermOk: false,
  parkingType: '',
  parkingSpaces: '',
  parkingPrice: '',
  parkingExtraNote: '',
  parkingEv: false,
  parkingBike: false,
  parkingLocker: false,
  electromenagersMode: '',
  electromenagersPaidNote: '',
  amenities: Object.fromEntries(AMENITY_KEYS.map(k => [k, false])) as Record<
    string,
    boolean
  >,
  listingPhotos: [],
  listingTitle: '',
  description: '',
  supplementaryInfo: '',
  guestEmail: '',
  desiredMoveInDate: '',
  contractEndDate: '',
  availabilityDate: '',
  transferReason: '',
  transferIncentive: '',
  virtualTourLink: '',
  photosRightsConfirmed: false,
})

/** Canadian postal code ANA NAN (e.g. H1A 1A1); letters exclude D, F, I, O, Q, U, W, Z where required. */
const CANADIAN_POSTAL_CODE =
  /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/i

function toStringValue(value: unknown): string {
  if (value === undefined || value === null) return ''
  return String(value)
}

export function isValidCanadianPostalCode(input: string): boolean {
  const compact = input.trim().replace(/\s/g, '')
  if (compact.length !== 6) return false
  return CANADIAN_POSTAL_CODE.test(compact)
}

function resolveAddressGeoPoint(
  address:
    | {
        location?: { lat?: unknown; lng?: unknown } | null
        geometry?: { location?: { lat?: unknown; lng?: unknown } } | null
      }
    | null
    | undefined
): { lat: number; lng: number } | null {
  const candidates = [address?.location, address?.geometry?.location]
  for (const point of candidates) {
    const lat = Number(point?.lat)
    const lng = Number(point?.lng)
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng }
    }
  }
  return null
}

/** YYYY-MM-DD for `<input type="date" />` from API / ISO strings. */
export function toAvailabilityInputValue(raw: unknown): string {
  const s = toStringValue(raw).trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** YYYY-MM for `<input type="month" />` from a full date or month string. */
export function toContractEndMonthInputValue(raw: unknown): string {
  const s = toStringValue(raw).trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 7)
  if (/^\d{4}-\d{2}$/.test(s)) return s
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

/** Contract end from YYYY-MM → YYYY-MM-DD (last day of that month) for the API. */
export function lastDayOfMonthIso(yearMonth: string): string | undefined {
  const m = yearMonth.trim().match(/^(\d{4})-(\d{2})$/)
  if (!m) return undefined
  const y = Number(m[1])
  const mo = Number(m[2])
  if (mo < 1 || mo > 12 || y < 1970 || y > 2100) return undefined
  const last = new Date(y, mo, 0)
  const dd = String(last.getDate()).padStart(2, '0')
  return `${m[1]}-${m[2]}-${dd}`
}

export function buildRegisterPayload(
  form: CreateAdFlowForm,
  accountEmail?: string
): Record<string, unknown> {
  const servicesOn = SERVICE_KEYS.filter(k => {
    if (k === 'electromenagers') return form.electromenagersMode === 'included'
    return form.services[k]
  })
  const amenitiesOn = AMENITY_KEYS.filter(k => form.amenities[k])
  const rentNum = parseMoneyForApi(form.monthlyRent)
  const availability =
    form.availabilityDate.trim() !== '' ? form.availabilityDate.trim() : undefined
  const contractEnd =
    form.contractEndDate.trim() !== '' ? form.contractEndDate.trim() : undefined
  const desiredMoveIn =
    form.desiredMoveInDate.trim() !== '' ? form.desiredMoveInDate.trim() : undefined
  const transferIncentive = parseMoneyForApi(form.transferIncentive)

  return {
    listingType: form.listingType || 'lease_transfer',
    unitType: form.unitType,
    address: {
      street: form.street,
      unit: form.unit,
      city: form.city,
      postalCode: form.postalCode,
      neighborhood: form.neighborhood,
      province: form.province || 'QC',
      country: form.country || 'Canada',
      ...(form.latitude != null && form.longitude != null
        ? { latitude: form.latitude, longitude: form.longitude }
        : {}),
    },
    monthlyRent: rentNum,
    rooms: form.roomCount,
    bathrooms: form.bathrooms,
    floor: serializeFloorForApi(form.floor, form.floorCustom),
    areaSqFt: form.areaSqFt.replace(/\D/g, ''),
    servicesIncluded: servicesOn,
    ...(form.electromenagersMode
      ? { electromenagersMode: form.electromenagersMode }
      : {}),
    ...(form.electromenagersMode === 'paid' && form.electromenagersPaidNote.trim()
      ? { electromenagersPaidNote: form.electromenagersPaidNote.trim() }
      : {}),
    laundry: form.laundry,
    petsAllowed:
      form.pets === 'conditions' || form.pets === 'yes' ? 'yes' : form.pets,
    ...(petsAllowsConditionsNote(form.pets) && form.petsConditionsNote.trim()
      ? { petsConditionsNote: form.petsConditionsNote.trim() }
      : {}),
    smokerAllowed: form.smokerAllowed,
    pmrAccessible: form.pmrAccessible,
    shortTermPossible: form.shortTermOk,
    parking: {
      type: form.parkingType,
      spaces: form.parkingSpaces,
      evCharging: form.parkingEv,
      bikeSpace: form.parkingBike,
      storageLocker: form.parkingLocker,
      ...(form.parkingType &&
      form.parkingType !== 'none' &&
      parseMoneyForApi(form.parkingPrice)
        ? { monthlyPrice: parseMoneyForApi(form.parkingPrice) }
        : {}),
      ...(form.parkingExtraNote.trim() ? { extraNote: form.parkingExtraNote.trim() } : {}),
    },
    buildingAmenities: amenitiesOn,
    title: form.listingTitle.trim(),
    description: form.description,
    supplementaryInfo: form.supplementaryInfo.trim(),
    images: listingPhotosToFlatUrls(form.listingPhotos),
    listingPhotos: serializeListingPhotos(form.listingPhotos),
    email: accountEmail?.trim().toLowerCase() || form.guestEmail.trim().toLowerCase() || undefined,
    availability,
    contractEnd,
    desiredMoveInDate: desiredMoveIn,
    ...(form.transferReason !== '' ? { transferReason: form.transferReason } : {}),
    ...(transferIncentive !== '' ? { transferIncentive } : {}),
    virtualTour: form.virtualTourLink.trim(),
    photosRightsConfirmed: form.photosRightsConfirmed,
  }
}

function isTransferReason(value: unknown): value is TransferReason {
  return typeof value === 'string' && (TRANSFER_REASON_KEYS as readonly string[]).includes(value)
}

function isListingType(value: unknown): value is ListingType {
  return typeof value === 'string' && (LISTING_TYPE_KEYS as readonly string[]).includes(value)
}

function toRoomCount(value: unknown): RoomCount | '' {
  let raw = toStringValue(value).trim().toLowerCase()
  if (!raw) return ''
  raw = raw
    .replace(/\s+/g, '')
    .replace(',', '.')
    .replace('½', '.5')
    .replace(/1\/2/g, '.5')

  if (raw.includes('studio') || raw === '0' || raw === '0.0' || raw === '1' || raw === '1.5') {
    return 'studio'
  }
  if (raw === '2.5' || raw === '2') return '2.5'
  if (raw === '3.5' || raw === '3') return '3.5'
  if (raw === '4.5' || raw === '4') return '4.5'
  if (raw === '5.5+' || raw.startsWith('5.5') || raw === '5') return '5.5+'
  const n = Number(raw)
  if (!Number.isNaN(n) && n >= 6) return '5.5+'
  return ''
}

function formatStreetFromAddress(address: Record<string, unknown> | null | undefined): string {
  if (!address || typeof address !== 'object') return ''
  const number = toStringValue(address.number).trim()
  const street = toStringValue(address.street).trim()
  if (!number) return street
  if (!street) return number
  const streetLower = street.toLowerCase()
  const numberLower = number.toLowerCase()
  if (streetLower === numberLower || streetLower.startsWith(`${numberLower} `)) {
    return street
  }
  return `${number} ${street}`
}

function detailsFlag(
  details: Record<string, unknown> | null | undefined,
  camelKey: string,
  snakeKey: string
): boolean {
  if (!details || typeof details !== 'object') return false
  return Boolean(details[camelKey] ?? details[snakeKey])
}

export function mapListingToCreateAdForm(
  listing: Record<string, any> | null | undefined,
  guestEmail?: string
): CreateAdFlowForm {
  const base = initialCreateAdFlowForm()
  if (!listing) return base

  const metaRaw = listing.listingMeta ?? listing.listing_meta ?? {}
  const meta =
    typeof metaRaw === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(metaRaw)
            return typeof parsed === 'object' && parsed !== null ? parsed : {}
          } catch {
            return {}
          }
        })()
      : metaRaw && typeof metaRaw === 'object'
        ? metaRaw
        : {}
  const servicesIncluded = Array.isArray(meta.servicesIncluded) ? meta.servicesIncluded : []
  const buildingAmenities = Array.isArray(meta.buildingAmenities) ? meta.buildingAmenities : []
  const parking = meta.parking && typeof meta.parking === 'object' ? meta.parking : {}
  const detailsRaw = listing.details
  const details =
    detailsRaw && typeof detailsRaw === 'object' ? (detailsRaw as Record<string, unknown>) : null

  const services: Record<string, boolean> = { ...base.services }
  SERVICE_KEYS.forEach(k => {
    if (k === 'electromenagers') {
      services[k] = false
      return
    }
    if (servicesIncluded.includes(k)) {
      services[k] = true
      return
    }
    // Legacy boolean columns when listing_meta.servicesIncluded is empty.
    if (k === 'chauffage') services[k] = detailsFlag(details, 'heat', 'heat')
    else if (k === 'electricite') services[k] = detailsFlag(details, 'electricity', 'electricity')
    else if (k === 'internet') services[k] = detailsFlag(details, 'internet', 'internet')
    else if (k === 'meuble') services[k] = detailsFlag(details, 'furnished', 'furnished')
    else services[k] = false
  })

  const electromenagersMode = normalizeElectromenagersFromApi(meta)

  const amenities: Record<string, boolean> = { ...base.amenities }
  AMENITY_KEYS.forEach(k => {
    amenities[k] = buildingAmenities.includes(k)
  })

  const parsedFloor = parseFloorFromApi(meta.floor)
  const address =
    listing.address && typeof listing.address === 'object'
      ? (listing.address as Record<string, unknown>)
      : null
  const geoPoint = resolveAddressGeoPoint(address)
  const rawPostalCode = toStringValue(address?.zipCode || address?.zip_code)
  const listingPhotos = hydrateListingPhotos(
    meta.listingPhotos ?? meta.listing_photos,
    listing.images
  )

  const petsFromMeta = normalizePetsForForm(toStringValue(meta.petsAllowed ?? meta.pets_allowed))
  const petsFallback =
    petsFromMeta ||
    (detailsFlag(details, 'petFriendly', 'pet_friendly') ? 'yes' : '')

  const parkingTypeFromMeta = toStringValue(parking.type) as ParkingType | ''
  const parkingTypeFallback =
    parkingTypeFromMeta ||
    (detailsFlag(details, 'parking', 'parking') ? 'outdoor' : '')

  return {
    ...base,
    listingType: isListingType(meta.listingType) ? meta.listingType : base.listingType,
    unitType: toStringValue(meta.unitType || listing.unitType),
    street: formatStreetFromAddress(address),
    unit: toStringValue(address?.doorNumber || address?.door_number),
    city: toStringValue(address?.city),
    postalCode: rawPostalCode ? formatCanadianPostalCodeInput(rawPostalCode) : '',
    neighborhood: toStringValue(address?.neighborhood),
    province: toStringValue(address?.province) || base.province,
    country: toStringValue(address?.country) || base.country,
    latitude: geoPoint?.lat ?? null,
    longitude: geoPoint?.lng ?? null,
    monthlyRent: digitsOnlyMoney(toStringValue(listing.price)),
    roomCount: toRoomCount(listing.bedrooms),
    bathrooms: toStringValue(listing.bathrooms || '1'),
    floor: parsedFloor.floor,
    floorCustom: parsedFloor.floorCustom,
    areaSqFt: toStringValue(meta.areaSqFt ?? meta.area_sq_ft).replace(/\D/g, ''),
    services,
    electromenagersMode,
    electromenagersPaidNote: toStringValue(
      meta.electromenagersPaidNote ?? meta.electromenagers_paid_note
    ),
    laundry: toStringValue(meta.laundry) as LaundryOption | '',
    pets: petsFallback,
    petsConditionsNote: toStringValue(
      meta.petsConditionsNote ||
        meta.pets_conditions_note ||
        listing.petsConditionsNote ||
        listing.pets_conditions_note
    ),
    smokerAllowed: Boolean(meta.smokerAllowed ?? meta.smoker_allowed),
    pmrAccessible: Boolean(meta.pmrAccessible ?? meta.pmr_accessible),
    shortTermOk: Boolean(meta.shortTermPossible ?? meta.short_term_possible),
    parkingType: parkingTypeFallback,
    parkingSpaces: toStringValue(parking.spaces),
    parkingPrice: digitsOnlyMoney(
      toStringValue(parking.monthlyPrice ?? parking.price ?? '')
    ),
    parkingExtraNote: toStringValue(parking.extraNote ?? parking.extra_note),
    parkingEv: Boolean(parking.evCharging ?? parking.ev_charging),
    parkingBike: Boolean(parking.bikeSpace ?? parking.bike_space),
    parkingLocker: Boolean(parking.storageLocker ?? parking.storage_locker),
    amenities,
    listingPhotos,
    listingTitle: toStringValue(listing.title),
    description: toStringValue(listing.description),
    supplementaryInfo: toStringValue(meta.supplementaryInfo ?? meta.supplementary_info),
    guestEmail: guestEmail || '',
    desiredMoveInDate: toAvailabilityInputValue(
      meta.desiredMoveInDate ??
        meta.desired_move_in_date ??
        listing.desiredMoveInDate ??
        listing.desired_move_in_date
    ),
    contractEndDate: toAvailabilityInputValue(
      listing.contractEnd ?? listing.contract_end
    ),
    availabilityDate: toAvailabilityInputValue(
      listing.availability ?? listing.availability_date
    ),
    transferReason: isTransferReason(meta.transferReason ?? meta.transfer_reason)
      ? ((meta.transferReason ?? meta.transfer_reason) as TransferReason)
      : '',
    transferIncentive: digitsOnlyMoney(
      toStringValue(
        meta.transferIncentive ??
          meta.transfer_incentive ??
          listing.transferIncentive ??
          listing.transfer_incentive
      )
    ),
    virtualTourLink: toStringValue(
      meta.virtualTour ?? meta.virtual_tour ?? listing.virtualTour ?? listing.virtual_tour
    ),
    photosRightsConfirmed: Boolean(
      meta.photosRightsConfirmed ??
        meta.photos_rights_confirmed ??
        listing.photosRightsConfirmed ??
        listing.photos_rights_confirmed ??
        listingPhotos.length > 0
    ),
  }
}
